import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Flame, ShoppingBag, MapPin } from "lucide-react";
import api from "../lib/api";
import { newEventId } from "../lib/track";

const G = "#C88A24", GB = "#E5A83A", INK = "#0B0806", IVORY = "#F4ECD9";
const MUTE = "#A7997E";

export function getVisitorId() {
    try {
        let v = localStorage.getItem("bp_vid");
        if (!v) { v = newEventId(); localStorage.setItem("bp_vid", v); }
        return v;
    } catch { return newEventId(); }
}

// 1 Nov 2026: the preview site shows the fixed-deadline mode before it is switched on live.
const IS_PREVIEW = typeof window !== "undefined" && /\.pages\.dev$/.test(window.location.hostname);

// Authoritative FOMO session: server drives the countdown + scarcity + price.
export function useFomoSession(fomo) {
    const [session, setSession] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [stockLeft, setStockLeft] = useState(null);
    const [salesShown, setSalesShown] = useState(0);
    const visitorId = useRef(getVisitorId());
    const floor = Number(fomo?.stock_floor ?? 1);
    // 3 Nov 2026: in fixed-deadline mode, show the launch price from the public config straight away.
    // Before this, the first paint (until /blueprint/session answered) showed "LAUNCH OFFER EXPIRED" and ₹699.
    const cfgDeadline = fomo?.deadline_on && fomo?.deadline ? String(fomo.deadline) : "";
    const secsToDeadline = (d) => { const t = Date.parse(d); return Number.isFinite(t) ? Math.max(0, Math.floor((t - Date.now()) / 1000)) : 0; };

    useEffect(() => {
        if (!fomo || !fomo.enabled) return;
        let alive = true;
        if (cfgDeadline) setSecondsLeft((s) => (s > 0 ? s : secsToDeadline(cfgDeadline)));
        const fallback = () => {
            if (!alive) return;
            if (cfgDeadline) { setSecondsLeft(secsToDeadline(cfgDeadline)); return; }
            const secs = Math.round((fomo.timer_minutes || 2) * 60);
            const start = fomo.stock_start ?? 7;
            setSession({ stock_left: start, city: "" });
            setSecondsLeft(secs);
            setStockLeft((s) => (s == null ? start : s));
        };
        const attempt = (retriesLeft) => {
            api.post("/blueprint/session", { visitor_id: visitorId.current, preview: IS_PREVIEW })
                .then((r) => { if (!alive) return; setSession(r.data); setSecondsLeft(r.data.seconds_left || 0); setStockLeft((s) => (s == null ? (r.data.stock_left ?? null) : s)); setSalesShown(r.data.sales_shown || 0); })
                .catch(() => {
                    if (retriesLeft > 0) setTimeout(() => attempt(retriesLeft - 1), 1200);
                    else fallback();
                });
        };
        attempt(1);
        return () => { alive = false; };
    }, [fomo]); // eslint-disable-line react-hooks/exhaustive-deps

    // A live sale: persist to server (IP-scoped, capped) and reflect the authoritative stock.
    const onRecentSale = useCallback(async () => {
        try {
            const { data } = await api.post("/blueprint/sale", { visitor_id: visitorId.current });
            setStockLeft(data.stock_left);
            setSalesShown(data.sales_shown);
        } catch {
            setStockLeft((s) => (s == null ? s : Math.max(floor, s - 1)));
            setSalesShown((n) => n + 1);
        }
    }, [floor]);

    useEffect(() => {
        const t = setInterval(() => setSecondsLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
        return () => clearInterval(t);
    }, []);

    const enabled = !!(fomo && fomo.enabled);
    const offerActive = enabled && secondsLeft > 0;
    const offerPrice = fomo?.offer_price ?? 499;
    const regularPrice = fomo?.regular_price ?? 999;
    const currentPrice = enabled ? (offerActive ? offerPrice : regularPrice) : null;

    return {
        visitorId: visitorId.current,
        session,
        secondsLeft,
        offerActive,
        currentPrice,
        offerPrice,
        regularPrice,
        // 1 Nov: Admin "stock" switch off = no "Only N left" badge (the public stock_left is always a number)
        stockLeft: fomo?.stock_enabled === false ? null : (stockLeft ?? session?.stock_left ?? fomo?.stock_left ?? null),
        onRecentSale,
        recentMax: Number(fomo?.popup_recent_max ?? 3),
        salesShown,
        timerMinutes: Number(fomo?.timer_minutes ?? 2),
        // 1 Nov: fixed launch deadline (same for everyone) when the server says so
        deadline: session ? (session.mode === "deadline" ? (session.deadline || "") : "") : cfgDeadline,
        city: session?.city || "",
        enabled,
    };
}

const two = (n) => String(n).padStart(2, "0");
const fmt = (s) => `${two(Math.floor(s / 60))}:${two(s % 60)}`;
// Days/hours for a fixed deadline that is days away; mm:ss under an hour
export const fmtLeft = (s) => {
    s = Math.max(0, Math.floor(s || 0));
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${two(h)}h ${two(m)}m`;
    if (h > 0) return `${h}h ${two(m)}m ${two(s % 60)}s`;
    return fmt(s);
};
// "15 Nov, 11:59 PM" in India time
export const fmtDeadline = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }).replace(" am", " AM").replace(" pm", " PM");
    } catch { return ""; }
};
const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

// Fires once when the visitor signals they're leaving (desktop mouse-to-top, mobile back button)
export function useExitIntent(active) {
    const [show, setShow] = useState(false);
    useEffect(() => {
        if (!active) return;
        try { if (sessionStorage.getItem("bp_exit_shown")) return; } catch { /* noop */ }
        let armed = true;
        const trigger = () => {
            if (!armed) return;
            armed = false;
            try { sessionStorage.setItem("bp_exit_shown", "1"); } catch { /* noop */ }
            setShow(true);
        };
        const onMouseOut = (e) => { if (e.clientY <= 0) trigger(); };
        document.addEventListener("mouseout", onMouseOut);
        // mobile: trap one back-press to surface the offer instead of leaving
        try { window.history.pushState({ bpExit: true }, ""); } catch { /* noop */ }
        const onPop = () => trigger();
        window.addEventListener("popstate", onPop);
        return () => { document.removeEventListener("mouseout", onMouseOut); window.removeEventListener("popstate", onPop); };
    }, [active]);
    return [show, () => setShow(false)];
}

// Fires once (per session) a moment after the launch timer expires — for a "you missed it, but…" lead grab.
export function useOfferExpired({ enabled, offerActive, ready, delayMs = 1500 }) {
    const [show, setShow] = useState(false);
    useEffect(() => {
        if (!enabled || !ready || offerActive) return;
        try { if (sessionStorage.getItem("bp_missed_shown")) return; } catch { /* noop */ }
        const t = setTimeout(() => {
            try { sessionStorage.setItem("bp_missed_shown", "1"); } catch { /* noop */ }
            setShow(true);
        }, delayMs);
        return () => clearTimeout(t);
    }, [enabled, ready, offerActive, delayMs]);
    return [show, () => setShow(false)];
}

export function MissedOfferModal({ open, onClose, offerPrice, regularPrice, discountPercent = 30, onLead, onGrab }) {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    if (!open) return null;
    const discounted = Math.round(regularPrice * (100 - discountPercent) / 100);
    const submitEmail = async () => {
        if (!email || !/.+@.+\..+/.test(email)) return;
        setBusy(true);
        try { await onLead?.(email); setSent(true); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-[92] flex items-center justify-center p-4" style={{ background: "rgba(6,4,3,0.82)" }} data-testid="bp-missed-modal" onClick={onClose}>
            <motion.div initial={{ scale: 0.85, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-md rounded-3xl border p-7 text-center" style={{ borderColor: `${G}55`, background: "#0E0B08" }}>
                <button onClick={onClose} data-testid="bp-missed-close" className="absolute right-4 top-4 text-white/40 hover:text-white/80 text-xl leading-none">×</button>
                <div className="mx-auto mb-3 h-14 w-14 rounded-full grid place-items-center" style={{ background: `linear-gradient(120deg,${G},${GB})` }}>
                    <Clock className="h-7 w-7" style={{ color: INK }} />
                </div>
                <p className="text-[11px] tracking-[0.25em] font-bold" style={{ color: GB }}>THE LAUNCH PRICE JUST CLOSED</p>
                <h3 className="mt-2 text-2xl sm:text-3xl font-semibold" style={{ fontFamily: "'Cormorant Garamond',serif", color: IVORY }}>
                    But clearly, you've got great taste.
                </h3>
                {sent ? (
                    <p className="mt-4 text-sm" data-testid="bp-missed-sent" style={{ color: GB }}>
                        ✓ Done! Check your inbox — your <b style={{ color: IVORY }}>{discountPercent}% OFF</b> code and payment link are on the way.
                    </p>
                ) : (
                    <>
                        <p className="mt-3 text-sm leading-relaxed" style={{ color: MUTE }}>
                            Leave your email and we'll personally hold your spot — plus drop a <b style={{ color: IVORY }}>{discountPercent}% OFF</b> code in your inbox right now, so you still pay just <b style={{ color: IVORY }}>{inr(discounted)}</b> instead of <span className="line-through">{inr(regularPrice)}</span>.
                        </p>
                        <div className="mt-5 flex gap-2">
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="bp-missed-email"
                                className="flex-1 rounded-full px-4 py-3 text-sm outline-none" style={{ background: "#160f08", border: `1px solid ${G}44`, color: IVORY }} />
                            <button onClick={submitEmail} disabled={busy} data-testid="bp-missed-email-submit"
                                className="rounded-full px-5 py-3 text-sm font-bold disabled:opacity-60" style={{ background: `linear-gradient(120deg,${G},${GB})`, color: INK }}>
                                {busy ? "…" : "Send my code"}
                            </button>
                        </div>
                        <p className="mt-3 text-[11px]" style={{ color: MUTE }}>No spam. Just your code + first access to the next window.</p>
                    </>
                )}
                <button onClick={() => { onClose(); onGrab && onGrab(); }} data-testid="bp-missed-buy"
                    className="mt-4 text-xs underline underline-offset-2" style={{ color: MUTE }}>
                    Or just get it now at {inr(regularPrice)}
                </button>
            </motion.div>
        </div>
    );
}

export function ExitIntentModal({ open, onClose, secondsLeft, offerPrice, regularPrice, onGrab, onLead, deadline = "" }) {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);
    if (!open) return null;
    const submitEmail = async () => {
        if (!email || !/.+@.+\..+/.test(email)) return;
        setBusy(true);
        try { await onLead?.(email); setSent(true); } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" style={{ background: "rgba(6,4,3,0.78)" }} data-testid="bp-exit-modal" onClick={onClose}>
            <motion.div initial={{ scale: 0.85, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-md rounded-3xl border p-7 text-center" style={{ borderColor: `${G}55`, background: "#0E0B08" }}>
                <button onClick={onClose} data-testid="bp-exit-close" className="absolute right-4 top-4 text-white/40 hover:text-white/80 text-xl leading-none">×</button>
                <div className="mx-auto mb-3 h-14 w-14 rounded-full grid place-items-center" style={{ background: `linear-gradient(120deg,${G},${GB})` }}>
                    <Flame className="h-7 w-7" style={{ color: INK }} />
                </div>
                <p className="text-[11px] tracking-[0.25em] font-bold" style={{ color: GB }}>WAIT — DON'T LOSE THIS</p>
                <h3 className="mt-2 text-2xl sm:text-3xl font-semibold" style={{ fontFamily: "'Cormorant Garamond',serif", color: IVORY }}>
                    Your launch price of {inr(offerPrice)} is still live
                </h3>
                {deadline ? (
                    <p className="mt-2 text-sm" style={{ color: MUTE }}>
                        It stays {inr(offerPrice)} till <b style={{ color: IVORY }}>{fmtDeadline(deadline)}</b>, then {inr(regularPrice)}.
                    </p>
                ) : (
                    <p className="mt-2 text-sm" style={{ color: MUTE }}>
                        Leave now and it goes back to <span className="line-through">{inr(regularPrice)}</span>.
                        {secondsLeft > 0 && <> Locks in <b style={{ color: IVORY }}>{fmt(secondsLeft)}</b>.</>}
                    </p>
                )}
                <button onClick={() => { onClose(); onGrab && onGrab(); }} data-testid="bp-exit-cta"
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 font-bold tracking-wide rounded-full transition-transform hover:-translate-y-0.5"
                    style={{ background: `linear-gradient(120deg,${G},${GB})`, color: INK }}>
                    Grab it at {inr(offerPrice)} now
                </button>
                {sent ? (
                    <p className="mt-4 text-sm" data-testid="bp-exit-sent" style={{ color: GB }}>✓ Sent! Check your inbox — we've saved your spot and the link.</p>
                ) : (
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: "#ffffff12" }}>
                        <p className="text-xs mb-2" style={{ color: MUTE }}>Not ready? Get the link + first dibs on the next offer:</p>
                        <div className="flex gap-2">
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="bp-exit-email"
                                className="flex-1 rounded-full px-4 py-2.5 text-sm outline-none" style={{ background: "#160f08", border: `1px solid ${G}44`, color: IVORY }} />
                            <button onClick={submitEmail} disabled={busy} data-testid="bp-exit-email-submit"
                                className="rounded-full px-4 py-2.5 text-sm font-bold disabled:opacity-60" style={{ border: `1px solid ${G}`, color: GB }}>
                                {busy ? "…" : "Send"}
                            </button>
                        </div>
                    </div>
                )}
            </motion.div>
        </div>
    );
}

// Countdown + price-jump urgency box
export function CountdownBox({ secondsLeft, offerActive, offerPrice, regularPrice, totalSeconds = 120, compact = false, deadline = "" }) {
    if (offerActive && deadline) {
        // 1 Nov: one fixed launch deadline for everyone (no per-visitor reset, no progress bar)
        return (
            <div data-testid="bp-countdown" className={`rounded-2xl border bg-[#0E0B08] ${compact ? "p-2.5" : "p-3.5"}`} style={{ borderColor: `${G}55` }}>
                <p className="flex items-center justify-center gap-2 text-[10px] sm:text-[11px] tracking-[0.16em] font-bold text-center" style={{ color: GB }}>
                    <Clock className="h-4 w-4 shrink-0" /> LAUNCH PRICE {inr(offerPrice)} TILL {fmtDeadline(deadline).toUpperCase()}
                </p>
                <div className="mt-1.5 flex items-baseline justify-center gap-3">
                    <span data-testid="bp-countdown-clock" style={{ fontFamily: "'Cormorant Garamond',serif", color: IVORY }} className="text-3xl sm:text-4xl tabular-nums font-semibold whitespace-nowrap">{fmtLeft(secondsLeft)}</span>
                    <span className="text-[11px]" style={{ color: MUTE }}>left · then {inr(regularPrice)}</span>
                </div>
            </div>
        );
    }
    if (offerActive) {
        return (
            <div data-testid="bp-countdown" className={`rounded-2xl border bg-[#0E0B08] ${compact ? "p-2.5" : "p-3.5"}`} style={{ borderColor: `${G}55` }}>
                <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 shrink-0 animate-pulse" style={{ color: GB }} />
                    <div className="flex-1 leading-tight">
                        <p className="text-[10px] tracking-[0.2em] font-bold" style={{ color: GB }}>LAUNCH PRICE ENDS IN</p>
                        <p className="text-[11px]" style={{ color: MUTE }}>Then it goes to <span className="line-through">{inr(regularPrice)}</span></p>
                    </div>
                    <div data-testid="bp-countdown-clock" style={{ fontFamily: "'Cormorant Garamond',serif", color: IVORY }} className="text-3xl sm:text-4xl tabular-nums font-semibold px-3 py-1 rounded-lg" >
                        {fmt(secondsLeft)}
                    </div>
                </div>
                <div className="mt-2 h-1 rounded-full bg-[#1c140b] overflow-hidden">
                    <div className="h-full rounded-full transition-[width] duration-1000 ease-linear"
                        style={{ width: `${Math.min(100, (secondsLeft / Math.max(1, totalSeconds)) * 100)}%`, background: `linear-gradient(90deg,${G},${GB})` }} />
                </div>
            </div>
        );
    }
    return (
        <div data-testid="bp-countdown-expired" className={`rounded-2xl border text-center ${compact ? "p-2.5" : "p-3.5"}`} style={{ borderColor: "#b9453855", background: "#160c0a" }}>
            <div className="flex items-center justify-center gap-3">
                <Clock className="h-5 w-5 shrink-0" style={{ color: "#e07a5f" }} />
                <div className="leading-tight text-center">
                    <p className="text-[11px] tracking-[0.16em] font-bold" style={{ color: "#e07a5f" }}>LAUNCH OFFER EXPIRED</p>
                    <p className="text-[11px]" style={{ color: MUTE }}>Price is now {inr(regularPrice)} — was {inr(offerPrice)}</p>
                </div>
            </div>
        </div>
    );
}

export function StockBadge({ stockLeft, offerActive = true }) {
    const [flash, setFlash] = useState(false);
    const prev = useRef(stockLeft);
    useEffect(() => {
        if (prev.current != null && stockLeft != null && stockLeft < prev.current) {
            setFlash(true);
            const t = setTimeout(() => setFlash(false), 1600);
            prev.current = stockLeft;
            return () => clearTimeout(t);
        }
        prev.current = stockLeft;
    }, [stockLeft]);
    if (stockLeft == null) return null;
    return (
        <span className="relative inline-flex">
            <span data-testid="bp-stock-badge" className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-wide px-3 py-2 rounded-full border transition-colors duration-300" style={flash ? { borderColor: "#e0555588", color: "#ff8a75", background: "#1c0d0b" } : { borderColor: `${G}66`, color: GB, background: "#160f08" }}>
                <Flame className={`h-3.5 w-3.5 ${flash ? "" : "animate-pulse"}`} /> Only {stockLeft} left{offerActive ? " at this price" : ""}
            </span>
            <AnimatePresence>
                {flash && (
                    <motion.span key={stockLeft} data-testid="bp-stock-flash"
                        initial={{ opacity: 0, y: 6, scale: 0.8 }} animate={{ opacity: 1, y: -14, scale: 1 }} exit={{ opacity: 0, y: -24 }}
                        transition={{ duration: 0.4 }}
                        className="absolute left-1/2 -translate-x-1/2 -top-1 whitespace-nowrap text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ background: "#e05555", color: "#fff" }}>
                        −1 · just sold!
                    </motion.span>
                )}
            </AnimatePresence>
        </span>
    );
}

// Simulated social-proof pop-ups (never stored, purely visual)
export function SalesPopups({ fomo, city, timerMinutes = 2, recentMax = 3, salesShown = 0, onRecentSale }) {
    const [current, setCurrent] = useState(null);
    const seen = useRef(new Set());
    const recentCount = useRef(0);
    const timers = useRef([]);

    useEffect(() => { recentCount.current = salesShown || 0; }, [salesShown]);

    const makeOne = useCallback((isRecent) => {
        const male = fomo?.names_male || [];
        const female = fomo?.names_female || [];
        const pool = [...male, ...female];
        const cities = fomo?.cities || [];
        if (!pool.length) return null;
        const maxAgo = fomo?.popup_max_minutes_ago || 35;
        const tm = Math.max(1, Math.round(timerMinutes));
        for (let i = 0; i < 14; i++) {
            const name = pool[Math.floor(Math.random() * pool.length)];
            const useCity = city && Math.random() < 0.55 ? city : (cities[Math.floor(Math.random() * cities.length)] || city);
            if (!useCity) continue;
            const key = `${name}|${useCity}`;
            if (seen.current.has(key)) continue;
            seen.current.add(key);
            // recent = "just bought" within the live timer window; older = beyond it
            const mins = isRecent
                ? 1 + Math.floor(Math.random() * tm)
                : (tm + 1) + Math.floor(Math.random() * Math.max(1, maxAgo - tm));
            return { name, city: useCity, mins, recent: isRecent, id: newEventId() };
        }
        return null;
    }, [fomo, city, timerMinutes]);

    useEffect(() => {
        if (!fomo || !fomo.popups_enabled) return;
        const minD = (fomo.popup_min_delay || 7) * 1000;
        const maxD = (fomo.popup_max_delay || 18) * 1000;
        let stopped = false;
        const clearAll = () => { timers.current.forEach(clearTimeout); timers.current = []; };
        const cycle = () => {
            if (stopped) return;
            const isRecent = recentCount.current < recentMax;
            const item = makeOne(isRecent);
            if (item) {
                if (item.recent) { recentCount.current += 1; onRecentSale && onRecentSale(); }
                setCurrent(item);
                timers.current.push(setTimeout(() => setCurrent(null), 5200));
            }
            const next = minD + Math.random() * (maxD - minD);
            timers.current.push(setTimeout(cycle, item ? next + 5200 : next));
        };
        timers.current.push(setTimeout(cycle, 4000));
        return () => { stopped = true; clearAll(); };
    }, [fomo, makeOne, recentMax, onRecentSale]);

    if (!fomo || !fomo.popups_enabled) return null;
    return (
        <div className="fixed left-3 sm:left-4 bottom-24 sm:bottom-28 z-[60] pointer-events-none" data-testid="bp-sales-popup-wrap">
            <AnimatePresence>
                {current && (
                    <motion.div key={current.id} data-testid="bp-sales-popup"
                        initial={{ opacity: 0, x: -40, scale: 0.9 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, x: -30, scale: 0.9 }}
                        transition={{ type: "spring", stiffness: 320, damping: 26 }}
                        className="flex items-center gap-3 max-w-[300px] rounded-2xl border px-3.5 py-2.5 shadow-2xl backdrop-blur-md"
                        style={{ borderColor: `${G}44`, background: "rgba(14,11,8,0.94)" }}>
                        <div className="h-9 w-9 shrink-0 rounded-full grid place-items-center" style={{ background: `linear-gradient(120deg,${G},${GB})` }}>
                            <ShoppingBag className="h-4.5 w-4.5" style={{ color: INK }} />
                        </div>
                        <div className="leading-tight">
                            <p className="text-[13px] font-semibold" style={{ color: IVORY }}>
                                {current.name} <span style={{ color: MUTE }} className="font-normal">from</span> {current.city}
                            </p>
                            <p className="text-[11px] flex items-center gap-1" style={{ color: MUTE }}>
                                <MapPin className="h-3 w-3" style={{ color: G }} /> bought the Blueprint · {current.mins} min ago
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
